import type { ReactNode } from 'react';
import './Card.css';

export interface CardProps {
  readonly children: ReactNode;
  readonly className?: string;
}

export interface CardSlotProps {
  readonly children: ReactNode;
  readonly className?: string;
}

export interface CardTitleProps extends CardSlotProps {
  /**
   * Heading level. Defaults to 3.
   *
   * A card title is a heading, and its level has to fit the page it lands on —
   * a fixed `<h3>` inside an `<h2>` section is a skipped level, which is one of
   * the few structural errors a screen-reader user navigating by heading will
   * actually trip over.
   */
  readonly level?: 2 | 3 | 4 | 5 | 6;
}

function cx(base: string, extra?: string): string {
  return extra ? `${base} ${extra}` : base;
}

function CardRoot({ children, className }: CardProps) {
  return <section className={cx('alc-card', className)}>{children}</section>;
}

/** Title row. Put `Card.Title` inside it, plus any actions. */
function Header({ children, className }: CardSlotProps) {
  return <div className={cx('alc-card-header', className)}>{children}</div>;
}

function Title({ children, className, level = 3 }: CardTitleProps) {
  const Tag = `h${level}` as const;
  return <Tag className={cx('alc-card-title', className)}>{children}</Tag>;
}

/** Supporting line under the title. */
function Description({ children, className }: CardSlotProps) {
  return <p className={cx('alc-card-description', className)}>{children}</p>;
}

function Body({ children, className }: CardSlotProps) {
  return <div className={cx('alc-card-body', className)}>{children}</div>;
}

/**
 * A titled panel — the kit's grouping primitive.
 *
 * Compose it with the attached slots, the same shape `AppHeader` uses:
 *
 * ```tsx
 * <Card>
 *   <Card.Header>
 *     <Card.Title>Resonant group</Card.Title>
 *     <Card.Description>Three states</Card.Description>
 *   </Card.Header>
 *   <Card.Body>…</Card.Body>
 * </Card>
 * ```
 *
 * It renders a `<section>` rather than a `<div>`, so a card with a title is a
 * navigable region rather than an anonymous box — which is the difference
 * between "heading, Resonant group" and nothing at all when someone is moving
 * through the page by landmark.
 *
 * Adopted from the vendored shadcn `card` that three chemistry apps carried.
 * The API is deliberately narrower: no `asChild`, no variant system, because
 * nothing in this repo used them.
 */
export const Card = Object.assign(CardRoot, { Header, Title, Description, Body });
